/**
 * Copyright © 2026 OwnersTable Inc. All rights reserved.
 * This source code is proprietary and confidential.
 * Unauthorized copying or distribution is strictly prohibited.
 */

/**
 * Lays a card count out across uniform-width rows, matching the Dashboard's statistics
 * cards. Splits into as many `columns`-wide rows as it takes, with any remainder landing
 * in a final short row rather than being padded out to a round number.
 */
export const getUniformRowCounts = (totalCards, columns) => {
  const rowCounts = [];
  let remainingCards = totalCards;

  while (remainingCards > 0) {
    rowCounts.push(Math.min(columns, remainingCards));
    remainingCards -= columns;
  }

  return rowCounts;
};

export const getLargeScreenRowCounts = (totalCards) => {
  if (totalCards <= 0) {
    return [];
  }

  const firstRowCount = Math.ceil(totalCards / 2);
  const secondRowCount = totalCards - firstRowCount;

  return secondRowCount > 0 ? [firstRowCount, secondRowCount] : [firstRowCount];
};

const greatestCommonDivisor = (left, right) => {
  if (!right) {
    return left;
  }

  return greatestCommonDivisor(right, left % right);
};

const leastCommonMultiple = (left, right) => {
  if (!left || !right) {
    return left || right;
  }

  return (left * right) / greatestCommonDivisor(left, right);
};

export const getLargeScreenColumnCount = (rowCounts) =>
  rowCounts.reduce((columnCount, rowCount) => leastCommonMultiple(columnCount, rowCount), 1);

export const getCardPosition = (index, rowCounts) => {
  let offset = 0;

  for (let rowIndex = 0; rowIndex < rowCounts.length; rowIndex += 1) {
    const cardsInRow = rowCounts[rowIndex];

    if (index < offset + cardsInRow) {
      return {
        rowIndex,
        columnIndex: index - offset,
        cardsInRow,
        isLastRow: rowIndex === rowCounts.length - 1,
      };
    }

    offset += cardsInRow;
  }

  return {
    rowIndex: 0,
    columnIndex: 0,
    cardsInRow: rowCounts[0] || 1,
    isLastRow: true,
  };
};

const BORDER = "1px solid #eee";

/**
 * Border and corner-radius styles for one card in a shared-border tile grid, plus an optional
 * `gridColumn: span` (when `gridColumnCount` is given) that stretches each row's cards to fill
 * the full width evenly — the technique that keeps an uneven last row (fewer cards than the
 * rows above it) from either leaving a dead gap or having one card wrongly stretch alone.
 *
 * Pass `includeOuterEdges` to have the cards draw the grid's outer frame themselves instead of
 * relying on a border around the container. The frame then follows the cards' real silhouette,
 * so a short trailing row closes off at its own width rather than the container's.
 */
export const getCardLayoutStyles = ({ index, rowCounts, gridColumnCount, includeOuterEdges }) => {
  const { rowIndex, columnIndex, cardsInRow, isLastRow } = getCardPosition(index, rowCounts);
  const isFirstRow = rowIndex === 0;
  const isFirstColumn = columnIndex === 0;
  const isLastColumn = columnIndex === cardsInRow - 1;
  // Without the outer frame, cards only draw dividers *between* each other. With it, each card
  // closes its own right/bottom edge, since a short row above cannot borrow the row below it.
  const styles = {
    borderRight: (includeOuterEdges ? !isLastColumn : isLastColumn) ? "none" : BORDER,
    borderBottom: includeOuterEdges || !isLastRow ? BORDER : "none",
    borderTopLeftRadius: isFirstRow && isFirstColumn ? 10 : 0,
    borderTopRightRadius: isFirstRow && isLastColumn ? 10 : 0,
    borderBottomLeftRadius: isLastRow && isFirstColumn ? 10 : 0,
    borderBottomRightRadius: isLastRow && isLastColumn ? 10 : 0,
  };

  // Only the top row draws a top edge — every other row's top is already the bottom of the row
  // above, so drawing both would thicken that divider to 2px.
  if (includeOuterEdges) {
    styles.borderLeft = BORDER;

    if (isFirstRow) {
      styles.borderTop = BORDER;
    }

    // A short row below leaves this card as the frame's bottom-right corner even though it is
    // not on the last row, so it has to round off there too. Not so when the rows are span-filled
    // (`gridColumnCount`) — every row then reaches the same right edge, which must stay straight.
    if (!gridColumnCount && isLastColumn && (rowCounts[rowIndex + 1] || 0) < cardsInRow) {
      styles.borderBottomRightRadius = 10;
    }
  }

  if (gridColumnCount) {
    styles.gridColumn = `span ${gridColumnCount / cardsInRow}`;
  }

  return styles;
};
